import { Component, inject, OnInit, OnDestroy, ChangeDetectionStrategy, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService, ConfigStateService, SessionStateService, PermissionService } from '@abp/ng.core';
import {
  ToasterService,
  ButtonComponent,
  CardComponent,
  CardHeaderComponent,
  CardBodyComponent,
  FormCheckboxComponent,
  ThemeSharedModule
} from '@abp/ng.theme.shared';
import { OAuthService } from 'angular-oauth2-oidc';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { CoreModule } from '@abp/ng.core';
import { finalize, switchMap, takeUntil, catchError, timeout } from 'rxjs/operators';
import { from, Subject, throwError, EMPTY } from 'rxjs';
import { RequestOtpDto, VerifyOtpDto, OtpResultDto } from '../../proxy/application/contracts/dtos/otp-verification/models';
import { environment } from '../../../environments/environment';
import { TwoFactorAuthService } from '../../proxy/application/services/two-factor-auth/two-factor-auth.service';

// Types for better type safety
interface User2FAInfo {
  requires2FA: boolean;
  email?: string;
}

interface LoginFormValue {
  username: string;
  password: string;
  rememberMe: boolean;
}

interface OtpFormValue {
  otpCode: string;
}

type LoginStep = 'login' | 'otp';

@Component({
  selector: 'app-custom-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CoreModule,
    ThemeSharedModule,
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    FormCheckboxComponent
  ],
  templateUrl: './custom-login.component.html',
  styleUrl: './custom-login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomLoginComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly oAuthService = inject(OAuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toasterService = inject(ToasterService);
  private readonly configState = inject(ConfigStateService);
  private readonly sessionState = inject(SessionStateService);
  private readonly http = inject(HttpClient);
  private readonly twoFactorAuthService = inject(TwoFactorAuthService);
  private readonly permissionService = inject(PermissionService);

  // Observable cleanup
  private readonly destroy$ = new Subject<void>();

  // Form controls with strong typing
  loginForm!: FormGroup<{
    username: FormControl<string>;
    password: FormControl<string>;
    rememberMe: FormControl<boolean>;
  }>;

  otpForm!: FormGroup<{
    otpCode: FormControl<string>;
  }>;

  // Reactive state using signals for better performance
  readonly isLoading = signal(false);
  readonly currentStep = signal<LoginStep>('login');
  readonly requires2FA = signal(false);
  readonly countdownText = signal('');
  readonly canResendOtp = signal(false);

  // Public state for template access
  userEmail?: string;
  private otpExpirationTime?: Date;
  private countdownInterval?: number;

  // Constants
  private readonly OTP_TIMEOUT_MS = 100000; // 10 seconds timeout for API calls
  private readonly OTP_VALIDITY_MINUTES = 5;

  // Getter methods for template reactive state
  get isLoginFormDisabled(): boolean {
    return this.loginForm.invalid || this.isLoading();
  }

  get isOtpFormDisabled(): boolean {
    return this.otpForm.invalid || this.isLoading();
  }

  get isResendOtpDisabled(): boolean {
    return this.isLoading() || !this.canResendOtp();
  }

  ngOnInit(): void {
    this.createForm();
    this.handleRouteParams();
    this.checkExistingAuth();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupCountdown();
  }

  private handleRouteParams(): void {
    const queryParams = this.route.snapshot.queryParams;

    // Store return URL
    const returnUrl = queryParams['returnUrl'];
    if (returnUrl && typeof returnUrl === 'string') {
      localStorage.setItem('returnUrl', returnUrl);
    }

    // Pre-fill username from registration
    const username = queryParams['username'];
    if (username && typeof username === 'string') {
      this.loginForm.patchValue({ username });
    }
  }

  private checkExistingAuth(): void {
    if (this.authService.isAuthenticated) {
      this.redirectByRole();
    }
  }

  private redirectByRole(): void {
    // Koç yetkisi varsa koç paneline yönlendir
    if (this.permissionService.getGrantedPolicy('Io.Coaches.ManageOwnStudents')) {
      this.router.navigate(['/coach/dashboard']);
      return;
    }

    // Admin yetkisi varsa dashboard'a yönlendir
    if (this.permissionService.getGrantedPolicy('AbpIdentity.Users')) {
      this.router.navigate(['/dashboard']);
      return;
    }

    // Öğrenci ise ödevlerime yönlendir
    this.router.navigate(['/student/my-assignments']);
  }

  private getDefaultRouteByRole(): string {
    // Koç yetkisi varsa koç paneline yönlendir
    if (this.permissionService.getGrantedPolicy('Io.Coaches.ManageOwnStudents')) {
      return '/coach/dashboard';
    }

    // Admin yetkisi varsa dashboard'a yönlendir
    if (this.permissionService.getGrantedPolicy('AbpIdentity.Users')) {
      return '/dashboard';
    }

    // Öğrenci ise ödevlerime yönlendir
    return '/student/my-assignments';
  }

  private cleanupCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = undefined;
    }
  }

  private createForm(): void {
    this.loginForm = this.fb.group({
      username: this.fb.control('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50)
      ]),
      password: this.fb.control('', [
        Validators.required,
        Validators.minLength(6)
      ]),
      rememberMe: this.fb.control(false)
    });

    this.otpForm = this.fb.group({
      otpCode: this.fb.control('', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
        Validators.pattern(/^[0-9]{6}$/)
      ])
    });
  }

  onSubmit(): void {
    if (this.currentStep() === 'login') {
      this.handleLogin();
    } else if (this.currentStep() === 'otp') {
      this.handleOtpVerification();
    }
  }

  private handleLogin(): void {
    // Ensure form values are synced before validation
    this.loginForm.updateValueAndValidity();

    if (this.loginForm.invalid) {
      this.markFormGroupTouched(this.loginForm);
      return;
    }

    this.isLoading.set(true);
    const formValue = this.loginForm.value as LoginFormValue;

    from(this.oAuthService.fetchTokenUsingPasswordFlow(formValue.username, formValue.password))
      .pipe(
        timeout(this.OTP_TIMEOUT_MS),
        switchMap(() => this.checkUser2FAStatus(formValue.username)),
        takeUntil(this.destroy$),
        finalize(() => this.isLoading.set(false)),
        catchError((error: HttpErrorResponse) => this.handleLoginError(error))
      )
      .subscribe({
        next: (user2FAInfo) => {
          if (user2FAInfo.requires2FA) {
            this.setupTwoFactorAuth(user2FAInfo);
          } else {
            this.completeLogin(formValue.rememberMe);
          }
        }
      });
  }

  private setupTwoFactorAuth(user2FAInfo: User2FAInfo): void {
    this.requires2FA.set(true);
    this.userEmail = user2FAInfo.email;
    this.currentStep.set('otp');
    this.sendOTP();
    this.toasterService.info('2FA etkin. Lütfen e-postanıza gönderilen kodu giriniz.');
  }

  private handleLoginError(error: HttpErrorResponse) {
    console.error('Login error:', error);

    const errorMessage = this.extractErrorMessage(error);
    this.toasterService.error(errorMessage, 'Giriş Başarısız');

    return EMPTY; // Complete the observable without emitting
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    return error?.error?.error_description ||
           (error as any)?.error_description ||
           error.message ||
           'Geçersiz kullanıcı adı veya şifre';
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private checkUser2FAStatus(username: string): Promise<User2FAInfo> {
    return new Promise((resolve) => {
      this.twoFactorAuthService.checkUserStatus({ usernameOrEmail: username })
        .pipe(
          timeout(this.OTP_TIMEOUT_MS),
          takeUntil(this.destroy$),
          catchError((error) => {
            console.error('Failed to check 2FA status:', error);
            // Fallback to no 2FA required if API fails
            return throwError(() => ({ requires2FA: false, email: undefined }));
          })
        )
        .subscribe({
          next: (response) => {
            resolve({
              requires2FA: response.isEnabled,
              email: response.email
            });
          },
          error: () => {
            resolve({
              requires2FA: false,
              email: undefined
            });
          }
        });
    });
  }

  private sendOTP(): void {
    if (!this.userEmail) {
      console.error('No user email available for OTP sending');
      return;
    }

    const requestOtpDto: RequestOtpDto = {
      email: this.userEmail,
      purpose: 'TWO_FACTOR_AUTH'
    };

    this.http.post<OtpResultDto>('/api/public/otp/send', requestOtpDto)
      .pipe(
        timeout(this.OTP_TIMEOUT_MS),
        takeUntil(this.destroy$),
        catchError((error: HttpErrorResponse) => {
          console.error('OTP sending error:', error);
          this.toasterService.error('2FA kodu gönderilirken bir hata oluştu.');
          return EMPTY;
        })
      )
      .subscribe({
        next: (result) => {
          if (result.success) {
            this.otpExpirationTime = new Date(Date.now() + (result.expiresInMinutes || this.OTP_VALIDITY_MINUTES) * 60 * 1000);
            this.startCountdown();
            this.toasterService.success(result.message || '2FA kodu e-postanıza gönderildi.');
          } else {
            this.toasterService.error(result.message || '2FA kodu gönderilemedi.');
          }
        }
      });
  }

  private handleOtpVerification(): void {
    if (this.otpForm.invalid) {
      this.markFormGroupTouched(this.otpForm);
      return;
    }

    if (!this.userEmail) {
      console.error('No user email available for OTP verification');
      this.toasterService.error('E-posta bilgisi bulunamadı');
      return;
    }

    this.isLoading.set(true);
    const formValue = this.otpForm.value as OtpFormValue;

    const verifyOtpDto: VerifyOtpDto = {
      email: this.userEmail,
      code: formValue.otpCode,
      purpose: 'TWO_FACTOR_AUTH'
    };

    this.http.post<OtpResultDto>('/api/public/otp/verify', verifyOtpDto)
      .pipe(
        timeout(this.OTP_TIMEOUT_MS),
        takeUntil(this.destroy$),
        finalize(() => this.isLoading.set(false)),
        catchError((error: HttpErrorResponse) => {
          console.error('OTP verification error:', error);
          this.toasterService.error('2FA doğrulanırken bir hata oluştu.');
          return EMPTY;
        })
      )
      .subscribe({
        next: (result) => {
          if (result.success) {
            const rememberMe = this.loginForm.get('rememberMe')?.value || false;
            this.completeLogin(rememberMe);
            this.toasterService.success(result.message || '2FA doğrulaması başarılı!');
          } else {
            this.toasterService.error(result.message || 'Geçersiz 2FA kodu');
          }
        }
      });
  }

  private completeLogin(rememberMe: boolean) {
    // Complete the login process
    if (rememberMe) {
      this.oAuthService.setupAutomaticSilentRefresh();
    }

    this.configState.refreshAppState().subscribe(() => {
      const storedReturnUrl = localStorage.getItem('returnUrl');
      localStorage.removeItem('returnUrl');

      // Eğer returnUrl /dashboard ise veya yoksa, role göre yönlendir
      if (!storedReturnUrl || storedReturnUrl === '/dashboard') {
        const defaultRoute = this.getDefaultRouteByRole();
        this.router.navigateByUrl(defaultRoute);
      } else {
        this.router.navigateByUrl(storedReturnUrl);
      }
    });
  }

  // Countdown management for OTP
  private startCountdown(): void {
    this.cleanupCountdown();

    this.canResendOtp.set(false);
    this.countdownInterval = window.setInterval(() => {
      if (!this.otpExpirationTime) {
        this.stopCountdown();
        return;
      }

      const now = new Date();
      const timeLeft = this.otpExpirationTime.getTime() - now.getTime();

      if (timeLeft <= 0) {
        this.canResendOtp.set(true);
        this.countdownText.set('Süre doldu');
        this.stopCountdown();
        return;
      }

      const minutes = Math.floor(timeLeft / 60000);
      const seconds = Math.floor((timeLeft % 60000) / 1000);
      this.countdownText.set(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    }, 1000);
  }

  private stopCountdown(): void {
    this.cleanupCountdown();
    this.canResendOtp.set(true);
  }

  resendOtp(): void {
    if (!this.canResendOtp()) return;
    this.sendOTP();
  }

  goBackToLogin(): void {
    this.currentStep.set('login');
    this.requires2FA.set(false);
    this.stopCountdown();
    this.otpForm.reset();
  }

  navigateToRegister(): void {
    this.router.navigate(['/account/register']);
  }

  navigateToForgotPassword(): void {
    this.router.navigate(['/account/forgot-password']);
  }

  loginWithGoogle(): void {
    this.isLoading.set(true);
    const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    // Environment'tan API URL'ini al
    const apiUrl = environment.apis.default.url;

    // ABP Backend'in Google OAuth endpoint'ine yönlendir
    window.location.href = `${apiUrl}/Account/Login?handler=ExternalLogin&provider=Google&returnUrl=${encodeURIComponent(returnUrl)}`;
  }

  // Getter methods for template access
  get isFormSubmittable(): boolean {
    if (this.currentStep() === 'login') {
      return this.loginForm.valid && !this.isLoading();
    } else {
      return this.otpForm.valid && !this.isLoading();
    }
  }

  get loginFormControls() {
    return this.loginForm.controls;
  }

  get otpFormControls() {
    return this.otpForm.controls;
  }
}
